import { useRef } from 'react';

import PropTypes from 'prop-types';

import CreateAccountModal from './CreateAccountModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import LoginModal from './LoginModal';
import UpgradeModal from './UpgradeModal';
import Toast from '../Toast';

/**
 * AuthModalManager Component
 * Orchestrates all authentication modals and handles transitions between them
 * Should be placed at the app root level (in AssistantLayout or App)
 *
 * @param {object} props - Component props
 * @param {string} props.activeModal - Currently active modal ('create-account' | 'login' | 'forgot-password' | 'upgrade' | null)
 * @param {Function} props.closeModal - Function to close all modals
 * @param {Function} props.openCreateAccount - Function to open create account modal
 * @param {Function} props.openLogin - Function to open login modal
 * @param {Function} props.openForgotPassword - Function to open forgot password modal
 * @param {string} [props.redirectTo] - URL to redirect to after successful login (defaults to current page)
 *
 * @returns {object} The AuthModalManager component
 */
function AuthModalManager({
  activeModal,
  closeModal,
  openCreateAccount,
  openLogin,
  openForgotPassword,
  redirectTo
}) {
  const toastRef = useRef(null);

  const handleCreateAccountSuccess = () => {
    toastRef.current?.show(`Welcome! Logging you in...`);
    // Note: Navigation happens automatically via loginWithPassword redirect
  };

  const handleLoginSuccess = () => {
    toastRef.current?.show(`Welcome back!`);
    // Note: Navigation happens automatically via loginWithPassword redirect
  };

  return (
    <>
      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={activeModal === 'create-account'}
        onClose={closeModal}
        onSuccess={handleCreateAccountSuccess}
        onSwitchToLogin={openLogin}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={activeModal === 'login'}
        redirectTo={redirectTo}
        onClose={closeModal}
        onSuccess={handleLoginSuccess}
        onSwitchToCreateAccount={openCreateAccount}
        onSwitchToForgotPassword={openForgotPassword}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={activeModal === 'forgot-password'}
        onClose={closeModal}
        onSwitchToLogin={openLogin}
      />

      {/* Upgrade Modal (for anonymous users) */}
      <UpgradeModal
        isOpen={activeModal === 'upgrade'}
        onClose={closeModal}
        onCreateAccount={openCreateAccount}
        onLogin={openLogin}
      />

      {/* Toast for success messages */}
      <Toast ref={toastRef} />
    </>
  );
}

AuthModalManager.propTypes = {
  activeModal: PropTypes.oneOf(['create-account', 'login', 'forgot-password', 'upgrade', null]),
  closeModal: PropTypes.func.isRequired,
  openCreateAccount: PropTypes.func.isRequired,
  openLogin: PropTypes.func.isRequired,
  openForgotPassword: PropTypes.func.isRequired,
  redirectTo: PropTypes.string
};

export default AuthModalManager;
